import type { ClassValue } from 'clsx';
import type { ButtonProps } from '@hdir/ui/button';
import type { FieldValues } from 'react-hook-form';
import type {
	BorderColor,
	BorderStyle,
	BorderWidth,
} from '../../tailwind/border';
import type {
	BgColor,
	Color,
} from '../../tailwind/color';

import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { Button } from '../Button';
import React from 'react';
import { cn } from '../Element';
import { FontAwesomeIconWrapper } from '../../components/FontAwesomeIconWrapper';
import { useFormContext } from './FormContext';


export function SubmitButton<TSchema extends FieldValues>({
	captcha = false,
	className,
	disabled: disabledProp,
	isLoading: isLoadingProp,
	isReCaptchaVerified,
	isSent,
	isSentSuccessful,
	size = 'medium',
	variant = 'primary',
	text: textProp = 'Submit',
	textWhenSendSuccessful = 'Sent',
	...otherButtonProps
}: Omit<ButtonProps, 'children'> & {
	captcha?: boolean;
	className?: ClassValue
	isReCaptchaVerified?: boolean;
	isSent?: boolean;
	isSentSuccessful?: boolean;
	text?: string;
	textWhenSendSuccessful?: string;
}): React.JSX.Element {
	const {
		formState: {
			isSubmitSuccessful,
			isSubmitted: isEverSubmitted,
			isSubmitting: doingSubmission,
			isValid,
		},
	} = useFormContext<TSchema>();
	const styleAsSuccess = isSentSuccessful;
	const disabled = disabledProp
		|| isReCaptchaVerified === false
		|| !isValid && (doingSubmission || isEverSubmitted)
		|| isSubmitSuccessful;

	const bgColor: keyof typeof BgColor | undefined =
		styleAsSuccess ? 'surfaceFeedbackSuccessDefaultImportant' :
			undefined;

	const color: keyof typeof Color | undefined =
		styleAsSuccess ? 'iconFeedbackSuccessDefaultImportant' :
			undefined;

	const borderColor: BorderColor | undefined =
		styleAsSuccess ? 'feedbackSuccessDefaultImportant' :
			undefined;

	const borderStyle: BorderStyle | undefined = styleAsSuccess ? 'solid' :	undefined;

	const borderWidth: BorderWidth | undefined = styleAsSuccess ? 1 : undefined;

	const icon =
		styleAsSuccess ? <FontAwesomeIconWrapper icon={faCircleCheck}/> :
			null;

	function textFromState(): string {
		if (isSent) {
			if (isSentSuccessful) return textWhenSendSuccessful;
		}
		return textProp;
	}
	const text = textFromState();

	return (
		<Button
			{...otherButtonProps}

			// TODO Does Frisk handle any of these?
			aria-disabled={disabled}
			aria-live='polite'
			// aria-label={isSubmitting ? textWhenSubmitting
			// 	: isValid ? 'Submit form'
			// 	: 'Form invalid, please correct errors'
			// }

			className={cn({
				borderColor,
				borderStyle,
				borderWidth,
				bgColor,
				color,
				className,
			})}
			data-captcha={captcha ? 'true' : 'false'}
			disabled={disabled}
			size={size}
			type='submit'
			variant={variant}
		>
			{icon}
			{text}
		</Button>
	);
}
